import { useEffect, useMemo, useRef, useState } from 'react'
import {
  signIn,
  signUp,
  signOut,
  fetchAuthSession,
  fetchUserAttributes,
  confirmSignIn,
  confirmSignUp,
  resendSignUpCode,
  resetPassword as amplifyResetPassword,
  confirmResetPassword,
  updatePassword,
  setUpTOTP,
  updateMFAPreference,
  fetchMFAPreference,
  associateWebAuthnCredential,
  listWebAuthnCredentials,
  deleteWebAuthnCredential
} from 'aws-amplify/auth'
import type {
  AuthWebAuthnCredential,
  ConfirmSignInOutput,
  SignInInput,
  SignInOutput,
  SignUpInput
} from 'aws-amplify/auth'
import { convertUserAttributesListToLabels, UserAttributes, PartnerRole, extractPartnerRoles } from 'src/common/static'
import httpClient from 'src/common/fetch/config'
import { history } from 'src/routes/history'
import { alertDialog, alertNotification } from 'src/ui'
import { io, Socket } from 'socket.io-client'
import { identifyUser, logEvent } from '../../analytics/analytics'

interface Headers {
  [key: string]: string
}

export interface State {
  isLoading: boolean
  user: UserAttributes | null
  roles: PartnerRole[]
  socket: Socket | null
}

export type AuthResultCallback = (authSuccess: boolean, response: string) => void

export interface AuthContext {
  user: UserAttributes | null
  roles: PartnerRole[]
  loading: boolean
  setUser: React.Dispatch<React.SetStateAction<State>>
  socket: Socket | null
  signup: (params: SignUpInput, cb: AuthResultCallback) => void
  login: (params: SignInInput) => Promise<SignInOutput>
  logout: () => void
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (username: string, code: string, password: string) => Promise<void>
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>
  verifyEmail: (username: string, code: string) => Promise<void>
  resendEmail: (username: string) => Promise<void>
  setupTOTP: () => Promise<string>
  getPreferredMfa: () => Promise<string>
  verifyTOTPSetup: (challengeAnswer: string) => Promise<string>
  mfaLogin: (challengeAnswer: string) => Promise<boolean>
  completeNewPassword: (newPassword: string) => Promise<ConfirmSignInOutput>
  // Passkeys (WebAuthn) + choice-based sign-in
  isPasskeySupported: () => boolean
  confirmSignInStep: (challengeResponse: string) => Promise<SignInOutput>
  registerPasskey: () => Promise<void>
  listPasskeys: () => Promise<AuthWebAuthnCredential[]>
  deletePasskey: (credentialId: string) => Promise<void>
}

const useProvideAuth = (): AuthContext => {
  const [state, setState] = useState<State>({
    isLoading: true,
    user: null,
    roles: [],
    socket: null
  })
  const lastRequest = useRef({ time: new Date().getTime(), location: history.location.pathname })

  const socket = useMemo(() => {
    const url = process.env.REACT_APP_SOCKET_URL as string
    return io(url, { transports: ['polling'], autoConnect: false })
  }, [])

  const setUser = async () => {
    try {
      const attributes = await fetchUserAttributes()
      const userAttributes = convertUserAttributesListToLabels(attributes)
      const session = await fetchAuthSession()
      const token = session.tokens?.idToken?.toString()
      // Cognito group membership lives on the access token (not the id token).
      const groups = session.tokens?.accessToken?.payload['cognito:groups'] as string[] | undefined
      const roles = extractPartnerRoles(groups)

      socket.auth = { token }
      socket.connect()

      setState({ user: userAttributes, roles, isLoading: false, socket })
    } catch {
      setState({ user: null, roles: [], isLoading: false, socket: null })
    }
  }

  const getAuthenticatedUser = async () => {
    try {
      return await fetchAuthSession()
    } catch {
      return null
    }
  }

  const jwtInterceptor = () => {
    httpClient.getInterceptors().request.use(async (request) => {
      const loggedUser = await getAuthenticatedUser()

      if (loggedUser) {
        // force user to refresh if they stay on the same page for more than 20 min without interacting with the server
        if (
          Math.abs(new Date().getTime() - lastRequest.current.time) / (60 * 1000) > 20 &&
          history.location.pathname === lastRequest.current.location
        ) {
          alertDialog(
            'Your time has expired',
            'Please refresh the page for any changes that might have occured.',
            false
          )
          // Create an abort signal to cancel the request
          const controller = new AbortController()
          controller.abort()
          return {
            ...request,
            signal: controller.signal
          }
        }

        lastRequest.current.time = new Date().getTime()
        lastRequest.current.location = history.location.pathname
        const session = await fetchAuthSession()
        const token = session.tokens?.idToken?.toString()
        if (token && request && request.headers) {
          ;(request.headers as unknown as Headers)['Authorization'] = `Bearer ${token}`
        }
      }

      return request
    })
  }

  const getCurrentAuthenticatedUser = async () => {
    try {
      const user = await fetchAuthSession({ forceRefresh: true })
      if (user) {
        setUser()
        const attributes = await fetchUserAttributes()
        const sub = attributes.sub
        identifyUser(sub as string)
        logEvent('session_restored', { userId: sub })
      } else {
        setState({ ...state, user: null, roles: [], isLoading: false })
      }
    } catch {
      setState({ ...state, user: null, isLoading: false })
    }
  }

  useEffect(() => {
    getCurrentAuthenticatedUser()
    jwtInterceptor()
  }, [])

  const login = async (params: SignInInput) => {
    return signIn(params)
  }

  const signup = async (params: SignUpInput, cb: AuthResultCallback) => {
    try {
      await signUp(params)
      cb(true, params.username)
    } catch (error) {
      cb(false, (error as Error).message)
    }
  }

  const logout = async () => {
    try {
      await signOut()
      state.socket?.disconnect()
      setState({
        socket: null,
        isLoading: false,
        user: null,
        roles: []
      })
    } catch (err) {
      alertNotification()
    }
  }

  const forgotPassword = async (email: string) => {
    try {
      await amplifyResetPassword({ username: email })
    } catch (err) {
      // email might not be registered.. do not display any error to the user
    }
  }

  const resetPassword = async (username: string, code: string, password: string) => {
    try {
      await confirmResetPassword({ username, confirmationCode: code, newPassword: password })
    } catch (err) {
      throw new Error((err as Error).message)
    }
  }

  const changePassword = async (oldPassword: string, newPassword: string) => {
    try {
      await updatePassword({ oldPassword, newPassword })
    } catch (err) {
      throw new Error((err as Error).message)
    }
  }

  const verifyEmail = async (username: string, code: string) => {
    try {
      await confirmSignUp({ username, confirmationCode: code })
    } catch (err) {
      throw new Error((err as Error).message)
    }
  }

  const resendEmail = async (username: string) => {
    try {
      await resendSignUpCode({ username })
    } catch (err) {
      throw new Error((err as Error).message)
    }
  }

  const setupTOTP = async (): Promise<string> => {
    try {
      const totpSetupDetails = await setUpTOTP()
      return totpSetupDetails.sharedSecret
    } catch (err) {
      throw new Error((err as Error).message)
    }
  }

  const verifyTOTPSetup = async (challengeAnswer: string): Promise<string> => {
    try {
      await confirmSignIn({ challengeResponse: challengeAnswer })
      await updateMFAPreference({ totp: 'PREFERRED' })
      await setUser()
      return 'SUCCESS'
    } catch (err) {
      throw new Error('Token unverified.')
    }
  }

  const getPreferredMfa = async (): Promise<string> => {
    try {
      const { preferred } = await fetchMFAPreference()
      return preferred === 'TOTP' ? 'SOFTWARE_TOKEN_MFA' : preferred ?? 'NOMFA'
    } catch (err) {
      throw new Error((err as Error).message)
    }
  }

  const mfaLogin = async (challengeAnswer: string): Promise<boolean> => {
    try {
      await confirmSignIn({ challengeResponse: challengeAnswer })
      await setUser()
      return true
    } catch (err) {
      const errMsg: string = (err as Error).message
      if (errMsg === 'Invalid code received for user') {
        throw new Error('Incorrect 2FA code, please try again.')
      } else {
        throw new Error(errMsg)
      }
    }
  }

  const completeNewPassword = async (newPassword: string): Promise<ConfirmSignInOutput> => {
    try {
      const result = await confirmSignIn({ challengeResponse: newPassword })
      if (result.isSignedIn) {
        await setUser()
      }
      return result
    } catch (err) {
      throw new Error((err as Error).message)
    }
  }

  // --- Passkeys (WebAuthn) ---

  // Feature-detect WebAuthn support so the login page can hide/disable the passkey option (R5).
  const isPasskeySupported = (): boolean =>
    typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined'

  // Advance the choice-based (USER_AUTH) sign-in by one step: select a first factor ('WEB_AUTHN' | 'PASSWORD'),
  // submit a password, or submit a TOTP code. Returns the next step so the login flow can branch; sets the user
  // once sign-in completes. Selecting 'WEB_AUTHN' runs the passkey ceremony internally.
  const confirmSignInStep = async (challengeResponse: string): Promise<SignInOutput> => {
    const result = await confirmSignIn({ challengeResponse })
    if (result.isSignedIn) {
      await setUser()
    }
    return result
  }

  // Enrollment ceremony — runs navigator.credentials.create() and registers the credential in Cognito.
  const registerPasskey = async (): Promise<void> => {
    await associateWebAuthnCredential()
  }

  const listPasskeys = async (): Promise<AuthWebAuthnCredential[]> => {
    const { credentials } = await listWebAuthnCredentials()
    return credentials
  }

  const deletePasskey = async (credentialId: string): Promise<void> => {
    await deleteWebAuthnCredential({ credentialId })
  }

  return {
    user: state.user,
    roles: state.roles,
    loading: state.isLoading,
    setUser: setState,
    socket: state.socket,
    signup,
    login,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    verifyEmail,
    resendEmail,
    setupTOTP,
    verifyTOTPSetup,
    getPreferredMfa,
    mfaLogin,
    completeNewPassword,
    isPasskeySupported,
    confirmSignInStep,
    registerPasskey,
    listPasskeys,
    deletePasskey
  }
}

export default useProvideAuth
